import { useCallback, useEffect, useState } from "react";
import { Megaphone, CheckCircle2, PhoneCall, RotateCcw, Phone, Mail, Building2, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Moderator inbox for advertising inquiries submitted through /advertise. */

const STATUSES = ["new", "contacted", "closed"];
const fmt = (iso) => new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

export default function AdminAdvertising() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");

  const load = useCallback(() => api.get("/advertising/inquiries").then((r) => setItems(r.data.items)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setSt = async (id, st) => {
    try { await api.put(`/advertising/inquiries/${id}/status`, { status: st }); load(); }
    catch { toast.error("Failed to update"); }
  };

  const filtered = items.filter((i) => !status || i.status === status);
  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: items.filter((i) => i.status === s).length }), {});

  return (
    <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-advertising-page">
      <h1 className="font-heading font-900 text-3xl sm:text-4xl flex items-center gap-3 mb-2">
        <Megaphone className="w-8 h-8 text-primary" /> Advertising Inquiries
      </h1>
      <p className="text-muted-foreground mb-6">Every inquiry submitted through the Advertise page — with scope, campaign preferences and contact details.</p>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button onClick={() => setStatus("")} data-testid="ad-filter-all"
          className={`px-3 py-1.5 text-sm border ${!status ? "border-primary bg-blue-50 font-700" : "border-zinc-300"}`}>All ({items.length})</button>
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`ad-filter-${s}`}
            className={`px-3 py-1.5 text-sm border capitalize ${status === s ? "border-primary bg-blue-50 font-700" : "border-zinc-300"}`}>
            {s} ({counts[s] || 0})
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.map((f) => (
          <div key={f.id} className={`border bg-white p-4 ${f.status === "new" ? "border-primary" : "border-zinc-300"}`} data-testid={`ad-inquiry-${f.id}`}>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-700">{f.first_name} {f.last_name}</span>
              {f.organization && <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Building2 className="w-3.5 h-3.5" /> {f.organization}</span>}
              <span className="text-muted-foreground text-xs">{fmt(f.created_at)}</span>
              <span className={`ml-auto text-xs font-700 px-2 py-0.5 capitalize ${f.status === "new" ? "bg-blue-100 text-blue-800" : f.status === "closed" ? "bg-zinc-100 text-zinc-500" : "bg-amber-100 text-amber-800"}`}>{f.status}</span>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {f.subjects?.map((s) => <span key={s} className="text-xs border border-violet-300 bg-violet-50 text-violet-800 px-2 py-0.5">{s === "Other" && f.subject_other ? `Other: ${f.subject_other}` : s}</span>)}
              {f.scope?.map((s) => <span key={s} className="text-xs border border-sky-300 bg-sky-50 text-sky-800 px-2 py-0.5">{s === "Other" && f.scope_other ? `Other: ${f.scope_other}` : s}</span>)}
            </div>
            {f.scope_details && <p className="text-xs text-indigo-700 mt-2"><b>Targets:</b> {f.scope_details}</p>}
            <p className="text-sm text-zinc-800 mt-2 whitespace-pre-line">{f.details}</p>
            {f.comments && <p className="text-sm text-zinc-600 mt-1 whitespace-pre-line border-l-2 border-zinc-200 pl-3">{f.comments}</p>}
            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
              <span className="inline-flex items-center gap-1"><Mail className="w-3.5 h-3.5" /> {f.email}</span>
              <span className="inline-flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {f.phone}</span>
              {(f.duration || f.start_date || f.budget) && (
                <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />
                  {[f.duration, f.start_date && `from ${f.start_date}`, f.budget].filter(Boolean).join(" · ")}
                </span>
              )}
              {f.contact_method && <span>Contact via: <b>{f.contact_method}</b></span>}
              <span className="ml-auto flex gap-2">
                {f.status === "new" && (
                  <button onClick={() => setSt(f.id, "contacted")} data-testid={`ad-mark-contacted-${f.id}`} className="inline-flex items-center gap-1 text-amber-700 font-700 hover:underline"><PhoneCall className="w-3.5 h-3.5" /> Mark contacted</button>
                )}
                {f.status !== "closed" && (
                  <button onClick={() => setSt(f.id, "closed")} data-testid={`ad-mark-closed-${f.id}`} className="inline-flex items-center gap-1 text-emerald-700 font-700 hover:underline"><CheckCircle2 className="w-3.5 h-3.5" /> Close</button>
                )}
                {f.status === "closed" && (
                  <button onClick={() => setSt(f.id, "new")} data-testid={`ad-reopen-${f.id}`} className="inline-flex items-center gap-1 text-zinc-500 font-700 hover:underline"><RotateCcw className="w-3.5 h-3.5" /> Reopen</button>
                )}
              </span>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-muted-foreground text-sm py-10 text-center">No advertising inquiries match this filter.</p>}
      </div>
    </div>
  );
}
